/**
 * Judge Assignment Service
 *
 * This service handles storing and retrieving judge category assignments in DynamoDB
 */

import { generateClient } from 'aws-amplify/data';
import { type Schema } from '../../amplify/data/resource';

// Initialize client lazily to avoid Amplify configuration warnings
let client: ReturnType<typeof generateClient<Schema>> | null = null;

const getClient = () => {
  if (!client) {
    client = generateClient<Schema>();
  }
  return client;
};

export interface JudgeAssignment {
  judgeEmail: string;
  assignedCategories: string[];
  assignedBy: string; // Super admin who assigned the categories
  assignedAt: string; // ISO timestamp
  updatedAt?: string; // ISO timestamp for updates
}

class JudgeAssignmentService {

  /**
   * Store judge category assignments in DynamoDB
   * This method will create a new assignment or update an existing one
   */
  async assignCategoriesToJudge(
    judgeEmail: string,
    categories: string[],
    assignedBy: string
  ): Promise<void> {
    try {
      // First, try to get existing assignment
      const existing = await getClient().models.JudgeAssignment.get({ judgeEmail });

      if (existing.data) {
        // Update existing assignment
        await getClient().models.JudgeAssignment.update({
          judgeEmail,
          assignedCategories: categories,
          assignedBy,
          updatedAt: new Date().toISOString()
        });
      } else {
        // Create new assignment
        const assignment = {
          judgeEmail,
          assignedCategories: categories,
          assignedBy,
          assignedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        await getClient().models.JudgeAssignment.create(assignment);
      }
    } catch (error) {
      console.error('Error in assignCategoriesToJudge:', error);
      throw error;
    }
  }

  /**
   * Get assigned categories for a specific judge from DynamoDB
   */
  async getAssignedCategories(judgeEmail: string): Promise<string[]> {
    const result = await getClient().models.JudgeAssignment.get({ judgeEmail });

    if (result.data && result.data.assignedCategories) {
      // Filter out null values and ensure we return string[]
      return result.data.assignedCategories.filter((cat): cat is string => cat !== null);
    }

    return [];
  }

  /**
   * Get all judge assignments from DynamoDB
   */
  async getAllJudgeAssignments(): Promise<JudgeAssignment[]> {
    const result = await getClient().models.JudgeAssignment.list();

    const assignments = (result.data || []).map(item => ({
      judgeEmail: item.judgeEmail,
      assignedCategories: item.assignedCategories?.filter((cat): cat is string => cat !== null) || [],
      assignedBy: item.assignedBy,
      assignedAt: item.assignedAt,
      updatedAt: item.updatedAt || undefined
    }));
    return assignments;
  }

  /**
   * Update assigned categories for a judge in DynamoDB
   */
  async updateAssignedCategories(
    judgeEmail: string,
    categories: string[]
  ): Promise<void> {
    try {
      // Check if the assignment exists first
      const existing = await getClient().models.JudgeAssignment.get({ judgeEmail });

      if (!existing.data) {
        throw new Error(`No assignment found for judge: ${judgeEmail}`);
      }

      await getClient().models.JudgeAssignment.update({
        judgeEmail,
        assignedCategories: categories,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating judge assignment:', error);
      throw error;
    }
  }

  /**
   * Remove assigned categories for a judge from DynamoDB
   */
  async removeAssignedCategories(judgeEmail: string): Promise<void> {
    await getClient().models.JudgeAssignment.delete({ judgeEmail });
  }

}

export const judgeAssignmentService = new JudgeAssignmentService();
